import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonGrid,
  IonItem,
  IonLabel,
  IonLoading,
  IonRow,
  IonText,
  IonTitle,
} from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import Content from '../components/Content';
import SongItem from '../components/SongItem';
import ConvertingUploader from '../components/ConvertingUploader';
import { useProfile } from '../data/store/useProfile';
import { useSongs } from '../data/store/useSongs';
import { useTransmission } from '../data/store/useTransmission';
import { MediaReference } from '../models/Song';
import { useIdParam } from '../util/sugar';


const ReviseDetail: React.FC = (props) => {
  const id = useIdParam(props);
  const history = useHistory();
  const { shouldFetch, fetch, revise, status } = useSongs();
  const { ownerId, authorId } = useProfile();
  const { fetchTodaysTransmission, clearTodayTransmission } = useTransmission();
  const { delete: deleteSong } = useSongs();
  const todaysTransmission = useTransmission(x => x.todaysTransmission);
  const song = useSongs(x => x.retrieve(id));

  const [description, setDescription] = useState('');
  const [media, setMedia] = useState<MediaReference | undefined>();
  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const isLoading = status === 'fetching';
  const isOwner = song && (
    (ownerId && song.ownerId === ownerId) ||
    (authorId && song.authors && song.authors.includes(authorId))
  );

  const songAlias = song?.authors
    ?.map(a => song?.artists?.[a]?.alias)
    .filter(Boolean)
    .join(' & ') || '';
  const downloadFilename = songAlias ? `${songAlias} - ${song?.title || ''}` : (song?.title || '');

  const reviseSong = () => {
    if (!media) return;
    const newTitle = titleRef.current?.value.trim() || '';
    const newDescription = descriptionRef.current?.value || description;
    revise(id, newDescription, media, newTitle, ownerId || undefined);
    history.push('/song-revisions/' + id);
  };

  useEffect(() => {
    ownerId && fetchTodaysTransmission(ownerId);
  }, [fetchTodaysTransmission, ownerId]);

  useEffect(() => {
    shouldFetch(id) && fetch(id);
    if (song) {
      setDescription(song.description);
    }
  }, [id, shouldFetch, fetch, song]);

  return (
    <Content className='background-wall'>
      <IonLoading isOpen={isLoading} />
      <IonGrid>
        <IonRow>
          <IonCol sizeMd='8' sizeLg='6' offsetMd='2' offsetLg='3'>

            {/* Ownership check — fix #2 */}
            {song && !isOwner && (
              <IonCard>
                <IonCardContent>
                  <IonText color="danger">You don't have permission to revise this song.</IonText>
                </IonCardContent>
              </IonCard>
            )}

            {/* Song not found and not loading — fix #3 */}
            {!song && !isLoading && !todaysTransmission && (
              <IonCard>
                <IonCardContent>
                  <IonText color="medium">Song not found.</IonText>
                </IonCardContent>
              </IonCard>
            )}

            {/* Revision form */}
            {song && isOwner && !todaysTransmission && ownerId && (
              <IonCard>
                <IonCardHeader>
                  <IonItem>
                    <IonTitle>
                      <IonLabel position='stacked' color='primary'>Original Title</IonLabel>
                      {song.title}
                    </IonTitle>
                  </IonItem>
                </IonCardHeader>
                <IonCardContent>
                  {/* Fix #1: native input instead of IonTextarea */}
                  <IonItem>
                    <IonLabel position='stacked' color='primary'>New Title</IonLabel>
                    <input
                      ref={titleRef}
                      type="text"
                      defaultValue=""
                      placeholder={song.title}
                      aria-label="New title for this revision"
                      style={{
                        width: '100%',
                        padding: '8px 0',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid var(--ion-color-light-shade)',
                        color: 'var(--ion-text-color)',
                        fontSize: '1rem',
                        outline: 'none',
                      }}
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position='stacked' color='primary'>Description</IonLabel>
                    <textarea
                      ref={descriptionRef}
                      key={song.id}
                      defaultValue={song.description}
                      rows={5}
                      aria-label="Song description"
                      style={{
                        width: '100%',
                        padding: '8px 0',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid var(--ion-color-light-shade)',
                        color: 'var(--ion-text-color)',
                        fontSize: '1rem',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                  </IonItem>
                  <IonItem>
                    <IonLabel position='stacked' color='primary'>Song Revision</IonLabel>
                    <ConvertingUploader
                      message='Select Song Revision file'
                      collection='songs'
                      downloadFilename={downloadFilename}
                      onComplete={(media) => {
                        if (media) setMedia(media);
                      }}
                    />
                  </IonItem>
                  <IonButton routerLink={'/song/' + id}>
                    Cancel
                  </IonButton>
                  <IonButton
                    disabled={!media}
                    onClick={reviseSong}
                    className='ion-float-right'
                  >
                    Transmit Revision
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            {/* Today's transmission already exists — fix #3: show regardless of whether song loaded */}
            {todaysTransmission && (
              <IonRow>
                <IonCol sizeMd='8' pushMd='2'>
                  <SongItem id={todaysTransmission} />
                  <IonButton
                    onClick={() => {
                      deleteSong(todaysTransmission);
                      clearTodayTransmission();
                    }}
                    expand='block'
                    color='danger'
                  >
                    Revoke
                  </IonButton>
                </IonCol>
              </IonRow>
            )}

          </IonCol>
        </IonRow>
      </IonGrid>
    </Content>
  );
};

export default ReviseDetail;
